import React, {useEffect, useRef, useState} from 'react';
import {linkHost, readVideoSource} from './videoSource';
import './VideoPreview.css';

// Превью записи над вопросами интервью. Плеер показываем, только когда его
// есть чем наполнить; иначе - спокойная плашка с тем, что случилось и куда идти.
// Сразу после загрузки файл на S3 может быть ещё недоступен: плеер получает
// ошибку, хотя через пару секунд всё играет. Поэтому до показа ошибки
// переспрашиваем файл несколько раз, а пока ждём - показываем лоадер.
export const RETRY_DELAY = 2000;
// Пауза растёт вдвое с каждой попыткой: 2с, 4с, 8с, ...
export const retryDelay = attempt => RETRY_DELAY * 2 ** attempt;
export const MAX_RETRIES = 8;

export default function VideoPreview({src, time, id}) {
    let source = readVideoSource(src);
    let video = useRef();
    let [failed, setFailed] = useState(false);
    let [attempt, setAttempt] = useState(0);
    let [waiting, setWaiting] = useState(false);

    useEffect(() => {
        setFailed(false);
        setAttempt(0);
        setWaiting(false);
    }, [src]);

    useEffect(() => {
        if (!waiting) return;
        let timer = setTimeout(() => {
            setAttempt(n => n + 1);
            setWaiting(false);
        }, retryDelay(attempt));
        return () => clearTimeout(timer);
    }, [waiting]);

    const onError = () => {
        if (attempt < MAX_RETRIES) setWaiting(true);
        else setFailed(true);
    };

    useEffect(() => {
        let el = video.current;
        if (!el || !time) return;
        el.currentTime = +(time.minutes || 0) * 60 + +(time.seconds || 0);
        let playing = el.play && el.play();
        playing && playing.catch && playing.catch(() => {});
    }, [time]);

    if (!source) {
        return <VideoNotice
            title={t('videoNotUploaded')}
            text="Добавьте ссылку на видеофайл в карточке интервью, и запись появится здесь."
        />;
    }

    if (source.kind === 'page') {
        return <VideoNotice
            title="Запись нельзя посмотреть здесь"
            text={`Ссылка ведёт на страницу ${source.place}, а не на видеофайл, поэтому встроенный плеер её не проиграет.`}
            href={source.src}
            action={`Открыть ${source.place}`}
        />;
    }

    if (failed) {
        let host = linkHost(source.src);
        return <VideoNotice
            title="Видео не загрузилось"
            text={`Файл по ссылке${host ? ' с ' + host : ''} недоступен или браузер не умеет его проигрывать.`}
            href={source.src}
            action="Открыть ссылку"
        />;
    }

    if (waiting) {
        return <div className="videoNotice videoLoading" role="status">
            <div className="videoSpinner" aria-hidden="true"></div>
            <div className="videoNoticeBody">
                <div className="videoNoticeTitle">Видео обрабатывается</div>
                <div className="videoNoticeText">Ждём, пока файл станет доступен. Это займёт несколько секунд.</div>
            </div>
        </div>;
    }

    return <video
        key={attempt}
        ref={video}
        controls
        preload="metadata"
        className="videoPreview"
        src={source.src}
        width="100%"
        id={id}
        onError={onError}
    />;
}

// Карточка интервью (libs/Smart) на всплытии гасит клик - preventDefault
// в обёртке поля, и браузер не переходит по ссылке. Клик по ссылке дальше
// не пускаем, чтобы вкладка открывалась.
const keepLinkClick = e => e.stopPropagation();

function VideoNotice({title, text, href, action}) {
    return <div className="videoNotice" role="note">
        <div className="videoNoticeIcon iconoir-video-camera-off" aria-hidden="true"></div>
        <div className="videoNoticeBody">
            <div className="videoNoticeTitle">{title}</div>
            <div className="videoNoticeText">{text}</div>
        </div>
        {href && <a className="videoNoticeAction" href={href} target="_blank" rel="noopener noreferrer" onClick={keepLinkClick}>{action}</a>}
    </div>;
}
